import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import {
    Auth,
    authState,
    confirmPasswordReset,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    signInWithPopup,
    updateProfile,
    GoogleAuthProvider,
    FacebookAuthProvider,
    User as FirebaseUser,
} from '@angular/fire/auth';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { doc, Firestore, onSnapshot, Unsubscribe } from '@angular/fire/firestore';
import { from, Observable, of, Subject } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { Rol, User } from '../../store/Authentication/auth.models';

// Action
import { loginFailure, logout, logoutSuccess, RegisterSuccess } from '../../store/Authentication/authentication.actions';

export interface Claims {
    rol?: Rol;
    empresaId?: string;
    permisos?: string[];
}

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
    /** Emite cuando los claims cambian server-side y ya se refrescó el token (ver refreshClaims()). */
    readonly claimsChanged$ = new Subject<void>();

    private claimsListenerUnsub?: Unsubscribe;
    private ultimoClaimsUpdatedAt: number | null = null;

    constructor(private store: Store, private auth: Auth, private firestore: Firestore, private functions: Functions) {
        authState(this.auth).subscribe((user) => {
            if (user) {
                localStorage.setItem('currentUser', JSON.stringify(this.toUser(user)));
                this.escucharCambiosDeClaims(user.uid);
            } else {
                localStorage.removeItem('currentUser');
                this.claimsListenerUnsub?.();
                this.claimsListenerUnsub = undefined;
                this.ultimoClaimsUpdatedAt = null;
            }
        });
    }

    /**
     * Un admin puede cambiarle los permisos a un sub_admin en cualquier
     * momento (actualizarPermisosMiembro bumpea `usuarios/{uid}.claimsUpdatedAt`).
     * Esta suscripción detecta ese cambio y fuerza un refresh del ID token
     * para que el afectado no tenga que cerrar sesión y volver a entrar.
     */
    private escucharCambiosDeClaims(uid: string): void {
        this.claimsListenerUnsub?.();
        this.ultimoClaimsUpdatedAt = null;

        this.claimsListenerUnsub = onSnapshot(doc(this.firestore, 'usuarios', uid), (snap) => {
            const ts = (snap.data()?.['claimsUpdatedAt'] as { toMillis?: () => number } | undefined)?.toMillis?.() ?? null;
            if (ts === null || ts === this.ultimoClaimsUpdatedAt) {
                return;
            }
            const esPrimeraLectura = this.ultimoClaimsUpdatedAt === null;
            this.ultimoClaimsUpdatedAt = ts;
            if (!esPrimeraLectura) {
                this.refreshClaims().then(() => this.claimsChanged$.next());
            }
        });
    }

    // Sign in with Google provider
    signInWithGoogle(): Promise<User> {
        return this.signInWithProvider(new GoogleAuthProvider());
    }

    // Sign in with Facebook provider
    signInWithFacebook(): Promise<User> {
        return this.signInWithProvider(new FacebookAuthProvider());
    }

    private async signInWithProvider(provider: GoogleAuthProvider | FacebookAuthProvider): Promise<User> {
        const result = await signInWithPopup(this.auth, provider);
        return this.toUser(result.user);
    }

    register(email: string, first_name: string, password: string): Observable<User> {
        return from(createUserWithEmailAndPassword(this.auth, email, password)).pipe(
            switchMap((credential) => from(updateProfile(credential.user, { displayName: first_name })).pipe(
                map(() => this.toUser(credential.user))
            )),
            tap((user) => this.store.dispatch(RegisterSuccess({ user }))),
            catchError((error) => {
                this.store.dispatch(loginFailure({ error: error.message }));
                throw error;
            })
        );
    }

    login(email: string, password: string): Observable<User> {
        return from(signInWithEmailAndPassword(this.auth, email, password)).pipe(
            switchMap((credential) => from(this.toUserConClaims(credential.user))),
            catchError((error) => {
                this.store.dispatch(loginFailure({ error: error.message }));
                throw error;
            })
        );
    }

    logout(): Observable<void> {
        this.store.dispatch(logout());

        return from(signOut(this.auth)).pipe(
            tap(() => {
                localStorage.removeItem('currentUser');
                localStorage.removeItem('token');
                this.store.dispatch(logoutSuccess());
            }),
            catchError(() => of(undefined))
        );
    }

    /** Pide el correo de "olvidé mi contraseña", mandado por Resend (no el nativo de Firebase). */
    resetPassword(email: string): Observable<void> {
        const callable = httpsCallable(this.functions, 'solicitarResetContrasena');
        return from(callable({ correo: email })).pipe(map(() => undefined));
    }

    /** Aplica la nueva contraseña con el oobCode del link del correo — 100% client-side, no necesita backend. */
    confirmarNuevaContrasena(oobCode: string, nuevaContrasena: string): Observable<void> {
        return from(confirmPasswordReset(this.auth, oobCode, nuevaContrasena));
    }

    /**
     * Returns the currently signed-in Firebase user, if any.
     *
     * Lee `this.auth.currentUser` (síncrono) en vez del campo poblado por la
     * suscripción a `authState()`, que es asíncrona: justo después de un
     * login (p.ej. al resolver el reto de doble factor en /auth/twostep),
     * `authState()` todavía no había disparado cuando el AuthGuard preguntaba
     * si había sesión, mandando al usuario a /landing como si no estuviera
     * logueado.
     */
    currentUser(): User | null {
        return this.auth.currentUser ? this.toUser(this.auth.currentUser) : null;
    }

    /**
     * Custom Claims del usuario logueado (rol/empresaId/permisos), leídos del
     * ID token. `authStateReady()` espera a que Firebase termine de
     * restaurar la sesión desde IndexedDB antes de mirar `currentUser`, para
     * no devolver null en el primer render tras un refresco de página.
     */
    async currentClaims(): Promise<Claims | null> {
        await this.auth.authStateReady();
        const user = this.auth.currentUser;
        if (!user) {
            return null;
        }
        const result = await user.getIdTokenResult();
        return {
            rol: result.claims['rol'] as Rol | undefined,
            empresaId: result.claims['empresaId'] as string | undefined,
            permisos: result.claims['permisos'] as string[] | undefined,
        };
    }

    /**
     * Fuerza un refresh del ID token para recoger claims cambiados server-side
     * (p.ej. un admin le agregó/quitó un permiso a este mismo usuario), sin
     * que tenga que cerrar sesión y volver a entrar.
     */
    async refreshClaims(): Promise<Claims | null> {
        const user = this.auth.currentUser;
        if (!user) {
            return null;
        }
        await user.getIdToken(true);
        return this.currentClaims();
    }

    private async toUserConClaims(firebaseUser: FirebaseUser): Promise<User> {
        const base = this.toUser(firebaseUser);
        const result = await firebaseUser.getIdTokenResult();
        return {
            ...base,
            uid: firebaseUser.uid,
            rol: result.claims['rol'] as Rol | undefined,
            empresaId: result.claims['empresaId'] as string | undefined,
            permisos: result.claims['permisos'] as string[] | undefined,
        };
    }

    private toUser(firebaseUser: FirebaseUser): User {
        const [firstName, ...rest] = (firebaseUser.displayName || '').split(' ');
        return {
            username: firebaseUser.email ?? undefined,
            email: firebaseUser.email ?? undefined,
            firstName: firstName || undefined,
            lastName: rest.join(' ') || undefined,
        };
    }
}
